import React, { useEffect, useState } from "react";
import { ErrorBox, Field, Options, UserOptions } from "./common";
import { errorMessage, listToText, locationsToText, nullIfBlank, numOrNull, projectCodeFrom } from "../dmApi";

function initialForm(project) {
  return {
    Tenant_Project_Id: project?.Tenant_Project_Id ? String(project.Tenant_Project_Id) : "",
    Project_Name: project?.Project_Name || "",
    Short_Code: (project?.Short_Code || "").toUpperCase(),
    Priority: project?.Priority || "P2",
    Stage: project?.Stage || "Content",
    Location: project?.Location || "",
    Keywords: listToText(project?.Keywords),
    Target_Locations: locationsToText(project?.Target_Locations),
    Start_Date: project?.Start_Date || "",
    End_Date: project?.End_Date || "",
    Assigned_To: project?.Assigned_To ? String(project.Assigned_To) : "",
    Prepared_By: project?.Prepared_By ? String(project.Prepared_By) : "",
    Reviewed_By: project?.Reviewed_By ? String(project.Reviewed_By) : "",
    Description: project?.Description || "",
  };
}

/** Form state and save for a DM project. Staff pass ``tenantId``; portal users act on their own tenant. */
export function useProjectForm({ api, project, tenantId, onSaved }) {
  const [form, setForm] = useState(() => initialForm(project));
  const [tenantProjects, setTenantProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const effectiveTenantId = project?.tenant_id || tenantId;
  const [company, setCompany] = useState({ name: "", id: effectiveTenantId || null });
  // Follows the project name until the user types their own code; clearing the code resumes auto-fill.
  const [codeTouched, setCodeTouched] = useState(() => Boolean(project?.Short_Code));

  useEffect(() => {
    let cancelled = false;
    if (api.isStaff && !effectiveTenantId) return undefined;
    api
      .overview(api.isStaff ? effectiveTenantId : undefined)
      .then((data) => {
        if (cancelled) return;
        setTenantProjects(data?.Tenant_Projects || []);
        setCompany({ name: data?.Business_Name || "", id: data?.tenant_id || effectiveTenantId || null });
      })
      .catch(() => {
        if (!cancelled) setTenantProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, effectiveTenantId]);

  const codeFor = (projectName) =>
    projectCodeFrom({ companyName: company.name, projectName, clientId: company.id });

  useEffect(() => {
    if (codeTouched) return;
    setForm((prev) => (prev.Project_Name ? { ...prev, Short_Code: codeFor(prev.Project_Name) } : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const set = (key) => (e) => {
    const value = e.target.value;
    if (key === "Short_Code") {
      setCodeTouched(value.trim() !== "");
      setForm((prev) => ({ ...prev, Short_Code: value.trim() ? value.toUpperCase() : codeFor(prev.Project_Name) }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "Project_Name" && !codeTouched ? { Short_Code: codeFor(value) } : {}),
    }));
  };

  const onLinkTenantProject = (e) => {
    const id = e.target.value;
    const linked = tenantProjects.find((p) => String(p.Tenant_Project_Id) === id);
    const name = form.Project_Name || linked?.Project_Name || "";
    setForm((prev) => ({
      ...prev,
      Tenant_Project_Id: id,
      Project_Name: name,
      Short_Code: codeTouched && prev.Short_Code ? prev.Short_Code : codeFor(name),
      Location: prev.Location || linked?.Address || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.Project_Name.trim()) {
      setError("Project name is required");
      return;
    }
    setSaving(true);
    setError("");
    const body = {
      Tenant_Project_Id: numOrNull(form.Tenant_Project_Id),
      Project_Name: form.Project_Name.trim(),
      Short_Code: nullIfBlank(form.Short_Code.trim().toUpperCase()),
      Priority: form.Priority,
      Stage: form.Stage,
      Location: nullIfBlank(form.Location.trim()),
      Keywords: form.Keywords,
      Target_Locations: form.Target_Locations,
      Start_Date: nullIfBlank(form.Start_Date),
      End_Date: nullIfBlank(form.End_Date),
      Assigned_To: numOrNull(form.Assigned_To),
      Prepared_By: numOrNull(form.Prepared_By),
      Reviewed_By: numOrNull(form.Reviewed_By),
      Description: nullIfBlank(form.Description),
    };
    try {
      const saved = project
        ? await api.updateProject(project.Dm_Project_Id, body)
        : await api.createProject({ ...body, tenant_id: api.isStaff ? effectiveTenantId : undefined });
      onSaved(saved);
    } catch (err) {
      setError(errorMessage(err, "Could not save project"));
    } finally {
      setSaving(false);
    }
  };

  return { form, set, onLinkTenantProject, tenantProjects, saving, error, setError, submit };
}

/** Create / edit a DM project. Staff pass ``tenantId``; portal users act on their own tenant. */
function ProjectForm({ api, meta, project, tenantId, onSaved, onCancel }) {
  const { form, set, onLinkTenantProject, tenantProjects, saving, error, submit } = useProjectForm({
    api,
    project,
    tenantId,
    onSaved,
  });

  return (
    <form onSubmit={submit}>
      <ErrorBox message={error} />
      <div className="dmp-form-grid">
        {tenantProjects.length > 0 ? (
          <Field label="Link to client's project / property">
            <select value={form.Tenant_Project_Id} onChange={onLinkTenantProject}>
              <option value="">Not linked</option>
              {tenantProjects.map((p) => (
                <option key={p.Tenant_Project_Id} value={p.Tenant_Project_Id}>
                  {p.Project_Name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label="Project name *">
          <input required value={form.Project_Name} onChange={set("Project_Name")} placeholder="e.g. Eastern Pride" />
        </Field>
        <Field label="Short code">
          <input
            value={form.Short_Code}
            onChange={set("Short_Code")}
            maxLength={20}
            placeholder="Auto: company-project-client ID"
            title="Generated as company letters, project letters and client ID; type to use your own"
          />
        </Field>
        <Field label="Priority">
          <select value={form.Priority} onChange={set("Priority")}>
            <Options values={meta?.priorities} />
          </select>
        </Field>
        <Field label="Stage">
          <select value={form.Stage} onChange={set("Stage")}>
            <Options values={meta?.stages} />
          </select>
        </Field>
        <Field label="Property / business location">
          <input value={form.Location} onChange={set("Location")} placeholder="Dhandumailaram, Hyderabad" />
        </Field>
        <Field label="Start date">
          <input type="date" value={form.Start_Date} onChange={set("Start_Date")} />
        </Field>
        <Field label="End date">
          <input type="date" value={form.End_Date} onChange={set("End_Date")} />
        </Field>
        <Field label="Assigned to">
          <select value={form.Assigned_To} onChange={set("Assigned_To")}>
            <UserOptions users={meta?.users} />
          </select>
        </Field>
        <Field label="Prepared by">
          <select value={form.Prepared_By} onChange={set("Prepared_By")}>
            <UserOptions users={meta?.users} blank="—" />
          </select>
        </Field>
        <Field label="Reviewed by">
          <select value={form.Reviewed_By} onChange={set("Reviewed_By")}>
            <UserOptions users={meta?.users} blank="—" />
          </select>
        </Field>
        <Field label="Keywords (comma separated)" full>
          <input value={form.Keywords} onChange={set("Keywords")} placeholder="villas in hyderabad, gated community" />
        </Field>
        <Field label="Target locations (comma separated, optional radius e.g. LB Nagar (R2km))" full>
          <input
            value={form.Target_Locations}
            onChange={set("Target_Locations")}
            placeholder="LB Nagar (R2km), BN Reddy (R3km), Hayatnagar"
          />
        </Field>
        <Field label="Description / brief" full>
          <textarea rows={3} value={form.Description} onChange={set("Description")} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        {onCancel ? (
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : project ? "Save changes" : "Create project"}
        </button>
      </div>
    </form>
  );
}

export default ProjectForm;
